import {
  ICandidateAggregateSearch,
  initialCandidateAggregateSearch,
} from 'app/ComponentLibrary/Filter/CandidateSearch';
import { ISelection } from 'app/ComponentLibrary/Filter/TypeAheadDropdown/CustomTypeAhead';
import _ from 'lodash';

export const candidateEngagementTabKeys = ['inquiry', 'engagement', 'referral', 'application', 'all'] as const;
export type CandidateEngagementTabKey = (typeof candidateEngagementTabKeys)[number];

export interface ICandidateEngagementFilterSnapshot {
  candidateFilter: ICandidateAggregateSearch;
  brandFilter: ISelection[];
  createdDateFilter: { startDateIso?: string; endDateIso?: string } | null;
  leadOrigin: string;
  activeTab: CandidateEngagementTabKey;
}

/** Restored filter state for the inquiries page (Date objects for date range). */
export interface ICandidateEngagementAppliedFromSnapshot {
  candidateFilter: ICandidateAggregateSearch;
  brandFilter: ISelection[];
  createdDateFilter: { startDate?: Date; endDate?: Date } | null;
  leadOrigin: string;
  activeTab: CandidateEngagementTabKey;
}

export function buildSnapshotFromApplied(params: {
  candidateFilterApplied: ICandidateAggregateSearch;
  brandFilterApplied: ISelection[];
  createdDateFilter: { startDate?: Date; endDate?: Date } | null;
  leadOriginApplied: string;
  activeTab: CandidateEngagementTabKey;
}): ICandidateEngagementFilterSnapshot {
  return {
    candidateFilter: _.cloneDeep(params.candidateFilterApplied),
    brandFilter: _.cloneDeep(params.brandFilterApplied),
    createdDateFilter:
      params.createdDateFilter?.startDate || params.createdDateFilter?.endDate
        ? {
            startDateIso: params.createdDateFilter?.startDate?.toISOString(),
            endDateIso: params.createdDateFilter?.endDate?.toISOString(),
          }
        : null,
    leadOrigin: params.leadOriginApplied,
    activeTab: params.activeTab,
  };
}

export function snapshotToEntries(snapshot: ICandidateEngagementFilterSnapshot): [string, unknown][] {
  return Object.entries({
    candidateFilter: snapshot.candidateFilter,
    brandFilter: snapshot.brandFilter,
    createdDateFilter: snapshot.createdDateFilter,
    leadOrigin: snapshot.leadOrigin,
    activeTab: snapshot.activeTab,
  });
}

export function entriesToAppliedState(entries: [string, unknown][]): ICandidateEngagementAppliedFromSnapshot {
  const obj = Object.fromEntries(entries) as Partial<ICandidateEngagementFilterSnapshot>;
  const tabRaw = obj.activeTab as string | undefined;
  const activeTab = candidateEngagementTabKeys.includes(tabRaw as CandidateEngagementTabKey)
    ? (tabRaw as CandidateEngagementTabKey)
    : 'inquiry';

  const dates = obj.createdDateFilter as { startDateIso?: string; endDateIso?: string } | null | undefined;
  const createdDateFilter =
    dates?.startDateIso || dates?.endDateIso
      ? {
          startDate: dates.startDateIso ? new Date(dates.startDateIso) : undefined,
          endDate: dates.endDateIso ? new Date(dates.endDateIso) : undefined,
        }
      : null;

  return {
    candidateFilter: obj.candidateFilter
      ? _.cloneDeep(obj.candidateFilter as ICandidateAggregateSearch)
      : { ...initialCandidateAggregateSearch },
    brandFilter: Array.isArray(obj.brandFilter) ? _.cloneDeep(obj.brandFilter as ISelection[]) : [],
    createdDateFilter,
    leadOrigin: typeof obj.leadOrigin === 'string' ? obj.leadOrigin : '',
    activeTab,
  };
}

export function sortFilterEntries(entries: [string, unknown][] = []) {
  return _.sortBy(entries, ([key]) => key);
}

export function normalizedSnapshotJson(entries: [string, unknown][]) {
  return JSON.stringify(sortFilterEntries(entries));
}

export function checkEngagementFiltersApplied(snapshot: ICandidateEngagementFilterSnapshot): boolean {
  const c = snapshot.candidateFilter;
  const hasCandidate =
    (c.selections?.length ?? 0) > 0 ||
    Object.keys(c).some(k => {
      if (k === 'selections') return false;
      const v = (c as Record<string, unknown>)[k];
      return v != null && v !== '';
    });
  const hasBrand = snapshot.brandFilter.length > 0;
  const hasDate = !!(snapshot.createdDateFilter?.startDateIso || snapshot.createdDateFilter?.endDateIso);
  const hasLead = !!snapshot.leadOrigin?.trim();
  const tabChanged = snapshot.activeTab !== 'inquiry';
  return hasCandidate || hasBrand || hasDate || hasLead || tabChanged;
}
